import { useState } from "react";
import { toast } from "sonner";
import { Paperclip, Send, X } from "lucide-react";
import { api, apiError } from "../lib/api";
import { PROJECT_TYPES, waLink } from "../lib/contact";

const initial = { name: "", phone: "", project_type: PROJECT_TYPES[0], message: "" };

export const QuoteForm = () => {
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Te rugăm să atașezi o imagine (schiță sau poză).");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Imaginea depășește 5 MB.");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const clearFile = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      fd.append("project_type", form.project_type);
      fd.append("message", form.message);
      if (file) fd.append("file", file);
      await api.post("/quote-requests", fd);
      toast.success("Cererea a fost salvată! Deschidem WhatsApp pentru o cotație rapidă.");
      const text = `Bună ziua! Sunt ${form.name}. Doresc o ofertă pentru: ${form.project_type}. ${
        form.message ? `Detalii: ${form.message}. ` : ""
      }(Telefon: ${form.phone})`;
      window.open(waLink(text), "_blank");
      setForm(initial);
      clearFile();
    } catch (err) {
      toast.error(apiError(err, "Nu am putut trimite cererea. Sună-ne direct!"));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-sm border border-line bg-cream px-4 py-3.5 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-clay/60 focus:border-oak";

  return (
    <form data-testid="quote-form" onSubmit={submit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="quote-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-walnut">
            Nume complet *
          </label>
          <input
            id="quote-name"
            data-testid="quote-name-input"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Numele tău"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="quote-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-walnut">
            Telefon *
          </label>
          <input
            id="quote-phone"
            data-testid="quote-phone-input"
            required
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            placeholder="07xx xxx xxx"
            className={inputCls}
          />
        </div>
      </div>

      <div>
        <label htmlFor="quote-project" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-walnut">
          Ce dorești să realizezi? *
        </label>
        <select
          id="quote-project"
          data-testid="quote-project-select"
          value={form.project_type}
          onChange={set("project_type")}
          className={inputCls}
        >
          {PROJECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="quote-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-walnut">
          Detalii & dimensiuni aproximative
        </label>
        <textarea
          id="quote-message"
          data-testid="quote-message-input"
          rows={4}
          value={form.message}
          onChange={set("message")}
          placeholder="Ex.: bucătărie colțar, approx. 3.2m x 2.4m, fronturi MDF vopsit..."
          className={`${inputCls} resize-none`}
        />
      </div>

      <div>
        <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-walnut">
          Atașează o schiță / poză (opțional)
        </span>
        {preview ? (
          <div className="flex items-center gap-4 rounded-sm border border-line bg-cream p-3">
            <img
              src={preview}
              alt="Previzualizare schiță"
              data-testid="quote-file-preview"
              className="h-16 w-16 rounded-sm object-cover"
            />
            <span className="flex-1 truncate text-sm text-clay">{file?.name}</span>
            <button
              type="button"
              data-testid="quote-file-remove-button"
              onClick={clearFile}
              className="grid h-9 w-9 place-items-center rounded-full border border-line text-clay transition-colors hover:border-oak hover:text-walnut"
              aria-label="Șterge atașamentul"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <label
            data-testid="quote-file-dropzone"
            className="flex cursor-pointer items-center justify-center gap-3 rounded-sm border border-dashed border-oak/50 bg-sand px-4 py-6 text-sm text-walnut transition-colors duration-300 hover:border-oak hover:bg-sand/70"
          >
            <Paperclip size={17} />
            Alege o imagine (max. 5 MB)
            <input
              type="file"
              accept="image/*"
              data-testid="quote-file-input"
              onChange={onFile}
              className="hidden"
            />
          </label>
        )}
      </div>

      <button
        type="submit"
        data-testid="quote-submit-button"
        disabled={sending}
        className="group flex h-14 w-full items-center justify-center gap-3 rounded-full bg-anthracite text-sm font-semibold uppercase tracking-[0.15em] text-cream transition-all duration-300 hover:bg-walnut disabled:opacity-60"
      >
        {sending ? "Se trimite..." : "Trimite cererea & continuă pe WhatsApp"}
        <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
      </button>

      <p className="text-center text-xs leading-relaxed text-clay">
        Trimite-ne dimensiunile aproximative sau o poză pe WhatsApp pentru o cotație de
        preț rapidă. Îți răspundem în cel mai scurt timp.
      </p>
    </form>
  );
};
